import { DesktopBlock } from '../utils/desktopBlockOrder';
import { getCardBackPath, CardBackDeck } from '../utils/cardBackArtwork';
import { ItemPreviewsProvider, MiskatonicTraumaProvider } from './ItemPresentationContext';
import { enabledBoards } from '../utils/gamePresentation';
import { findInvestigator, getFirstControlledInvestigator } from '../utils/investigators';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DeckInfo, GameLogEntry, GameState, Investigator, InvestigatorStatistics, Player, PlayerAchievementInGame, SkillBreakdown, UserAction, UserLanguage, WikiDeckType, gameApi } from '../api';
import { GameLog } from './GameLog';
import { GameData } from './GameData';
import { AdminGrantPanel } from './AdminGrantPanel';
import { AdminDeckPanel } from './AdminDeckPanel';
import { ActionsList } from './ActionsList';
import { TradeActions } from './TradeActions';
import { TabbedMapDisplay } from './TabbedMapDisplay';
import { DigitalMapDisplay } from './DigitalMapDisplay';
import { useDesktopBlockOrder, useMapStyle, useMonsterTokenStyle } from '../utils/clientSettings';
import { getUpkeepDecision, isControllerAction, isUndoAction, UpkeepActions } from './UpkeepActions';
import { GameActionSheet } from './GameActionSheet';
import { QuickActionRow } from './QuickActionRow';
import { quickActionPresentation } from './quickActionPresentation';
import { FightActions } from './FightActions';
import { useFightActionExecution } from './useFightActionExecution';
import { fightControlKind, useFightInvestigator } from './fightPresentation';
import type { InvestigatorFocusRequest } from './MapViewport';
import { useFittedGameLayout } from './useFittedGameLayout';
import { useMobileGameLayout } from './useMobileGameLayout';
import { jumpToGameSection } from './GameNavigation';
import { GameMessage } from './GameMessage';
import { MonsterInspectionContext, MonsterInspectionEnabled } from './MonsterInspector';
import { MonsterDetailCard, MonstersDetails } from './Monsters';
import { FinalBattlePanel } from './FinalBattlePanel';
import { AncientOneDisplay } from './AncientOneDisplay';
import { HeraldCard } from './HeraldCard';
import { Card } from './Card';
import { getItemKind } from './StackedItemCard';
import { WikiView } from './wiki/WikiView';
import { isSkillCheckAcknowledgment, isSkillCheckProgression } from './SkillCheckActionControls';
import { SkillCheckInfo } from './SkillCheckInfo';
import { AchievementCard } from './AchievementCard';
import { Support } from './Support';
import { GameDialog } from './GameDialog';
import { InvestigatorInspector } from './InvestigatorInspector';
import { orderedDockInvestigators, orderedGameInvestigators } from './investigatorOrder';
import { InvestigatorDisplay } from './InvestigatorDisplay';
import { PlayerHeader } from './PlayersList';
import { GameStatusSummary } from './GameStatusSummary';
import { GameMapFrame } from './GameMapFrame';
import { GameRules, AncientOneDetails, buildRuleGroups } from './GameRules';
import { LatestMythos } from './LatestMythos';
import { useLatestMythosPopup } from './useLatestMythosPopup';
import { GameDock, DockPopup, DockButtonRefs } from './GameDock';
import { GameTracksDetails } from './GameTracksDetails';
import './GameUI.css';
import { GuardianCard } from './GuardianCard';
import { InstitutionDisplay } from './InstitutionDisplay';
import { SpellCastingPreviewsProvider } from './SpellCastingContext';

export const getStatColor = (breakdown?: SkillBreakdown) => {
    if (!breakdown) return undefined;
    if (breakdown.total > breakdown.base) return '#3fb950';
    if (breakdown.total < breakdown.base) return '#6d0101';
    return undefined;
  };

const START_GAME_NOW_ACTION_ID = 'StartGameNow';

interface GameInfoProps {
  openedActionSessions?: Set<string>;
  gameState: GameState;
  loading: boolean;
  playerName: string;
  authRole?: string;
  message?: string;
  selectedInvestigator?: string;
  controlledInvestigators?: string[];
  currentInvestigator?: Investigator;
  userLanguage?: UserLanguage;
  availableActions: UserAction[];
  onJoinGame: () => void;
  onExecuteAction: (actionId: string) => void;
  onRefreshGameState?: () => void | Promise<void>;
  onSelectInvestigator?: (name: string) => void;
}

type DeckDisplayEntry = {
  id: CardBackDeck;
  label: string;
  imagePath: string;
  info: DeckInfo;
  wikiDeckType?: WikiDeckType;
};

type InvestigatorStatisticsTableEntry = {
  name: string;
  statistics: InvestigatorStatistics;
  isDevoured: boolean;
};

type NumericInvestigatorStatKey =
  | 'monstersDefeated'
  | 'gatesClosed'
  | 'gatesSealed'
  | 'damageToAncientOne'
  | 'diceRollSuccesses'
  | 'diceRolls'
  | 'movementsMade'
  | 'trainRides'
  | 'cluesSpent'
  | 'moneySpent'
  | 'staminaDamageTaken'
  | 'sanityDamageTaken'
  | 'knockouts'
  | 'timesDrivenInsane';

type StatisticRowDefinition = {
  key: NumericInvestigatorStatKey;
  label: string;
  preference: 'high' | 'low' | 'neutral';
  format?: (value: number) => string;
  skipZeros?: boolean;
};

const statisticRows: StatisticRowDefinition[] = [
  { key: 'monstersDefeated', label: 'Monsters Defeated', preference: 'high' },
  { key: 'gatesClosed', label: 'Gates Closed', preference: 'high' },
  { key: 'gatesSealed', label: 'Gates Sealed', preference: 'high' },
  { key: 'damageToAncientOne', label: 'Damage To Ancient One', preference: 'high', skipZeros: true },
  { key: 'diceRollSuccesses', label: 'Dice Roll Successes', preference: 'high' },
  { key: 'diceRolls', label: 'Dice Rolls', preference: 'high' },
  { key: 'movementsMade', label: 'Movement', preference: 'high' },
  { key: 'trainRides', label: 'Train Rides', preference: 'high' },
  { key: 'cluesSpent', label: 'Clues Spent', preference: 'high' },
  { key: 'moneySpent', label: 'Money Spent', preference: 'high', format: (value) => `$${value}` },
  { key: 'staminaDamageTaken', label: 'Stamina Damage Taken', preference: 'low' },
  { key: 'sanityDamageTaken', label: 'Sanity Damage Taken', preference: 'low' },
  { key: 'knockouts', label: 'Knockouts', preference: 'low' },
  { key: 'timesDrivenInsane', label: 'Times Driven Insane', preference: 'low' },
];

type RenderPlayerProps = {
  player: Player;
}
const RenderPlayer: React.FC<RenderPlayerProps> = ({player}) => {
  return (
    <li>{player.name}</li>
  );
}

type CreatedGameInfoProps = {
  gameState: GameState;
  loading: boolean;
  startGameNowAction?: UserAction;
  onExecuteAction: (actionId: string) => void;
}
const CreatedGameInfo: React.FC<CreatedGameInfoProps> = ({
  gameState,
  loading,
  startGameNowAction,
  onExecuteAction,
}) => {
  const players = gameState.players ?? [];

  return (
    <div>
      <h3>Players:</h3>
      <div>{players.length} / {gameState.gameSettings?.expectedPlayersCount || 0} <small>(The game would automatically start when there are enough players joined)</small></div>
      {startGameNowAction && (
        <div className="start-game-now-section">
          <button
            type="button"
            onClick={() => onExecuteAction(startGameNowAction.id)}
            disabled={loading || !!startGameNowAction.disabled}
            title={startGameNowAction.description}
          >
            Start game now
          </button>
        </div>
      )}
      <ul>
        {players.map((p) => <RenderPlayer key={p.name} player={p} />)}
      </ul>
      <h3>Settings:</h3>
      {gameState.gameSettings?.maxInvestigatorsCount != null && (
        <div><strong>Number of investigators:</strong> up to {gameState.gameSettings.maxInvestigatorsCount}</div>
      )}
      <div><strong>Investigator draw:</strong>&nbsp;{gameState.gameSettings?.investigatorDrawType || ''}</div>
      <div><strong>Ancient One draw:</strong>&nbsp;{gameState.gameSettings?.ancientOneSelectionType || ''}</div>
      <div><strong>Expansions:</strong>&nbsp;Base{gameState.gameSettings?.includeDunwichCards ? ", Dunwich" : ''}{gameState.gameSettings?.includeDarkPharaohCards ? ", Curse of the Dark Pharaoh" : ''}{gameState.gameSettings?.includeKingsportCards ? ", Kingsport" : ''}{gameState.gameSettings?.includeInsmouthCards ? ", Innsmouth" : ''}{gameState.gameSettings?.includeKingInYellowCards ? ", The King in Yellow" : ''}{gameState.gameSettings?.includeBlackGoatCards ? ", The Black Goat of the Woods" : ''}{gameState.gameSettings?.includeLurkerCards ? ", The Lurker at the Threshold" : ''}{gameState.gameSettings?.includeMiskatonicCards ? ", Miskatonic" : ''}{gameState.gameSettings?.includeFanMadeInvestigators ? ", Fan Made Investigators" : ''}</div>
      <div><strong>Enabled boards:</strong> Arkham{enabledBoards(gameState).dunwich ? ', Dunwich' : ''}{enabledBoards(gameState).kingsport ? ', Kingsport' : ''}{enabledBoards(gameState).innsmouth ? ', Innsmouth' : ''}</div>
      {gameState.gameSettings?.randomCluePlacement ? <div><strong>Random clue placement</strong></div> : null}
    </div>
  );
}

type ActiveEntitiesUnderMapProps = {
  gameState: GameState;
  isFinalBattlePhase: boolean;
};

const ActiveEntitiesUnderMap: React.FC<ActiveEntitiesUnderMapProps> = ({
  gameState,
  isFinalBattlePhase,
}) => {
  if (isFinalBattlePhase) {
    return null;
  }

  const activeHeralds = gameState.activeHeralds ?? [];
  const activeGuardians = gameState.activeGuardians ?? [];
  if (!gameState.ancientOne && activeHeralds.length === 0 && activeGuardians.length === 0 && !gameState.activeInstitution) {
    return null;
  }

  return (
    <div className="active-entities-under-map">
      {gameState.ancientOne && (
        <section className="active-ancient-under-map" aria-label="Active Ancient One">
          <h3>Ancient One</h3>
          <AncientOneDisplay ancientOne={gameState.ancientOne} responsive />
        </section>
      )}
      {activeHeralds.length > 0 && (
        <section className="active-heralds-under-map" aria-label="Active Heralds">
          <h3>Heralds</h3>
          <div className="active-heralds-list active-heralds-column">
            {activeHeralds.map((herald, index) => (
              <HeraldCard key={`${herald.name}-${index}`} herald={herald} />
            ))}
          </div>
        </section>
      )}
      {activeGuardians.length > 0 && (
        <section className="active-heralds-under-map" aria-label="Active Guardians">
          <h3>Guardians</h3>
          <div className="active-heralds-list">
            {activeGuardians.map((guardian, index) => (
              <GuardianCard key={`${guardian.name}-${index}`} guardian={guardian} />
            ))}
          </div>
        </section>
      )}
      {gameState.activeInstitution && (
        <section className="active-heralds-under-map active-institution-under-map" aria-label="Active Institution">
          <h3>Institution</h3>
          <InstitutionDisplay institution={gameState.activeInstitution} locations={gameState.locations} />
        </section>
      )}
      {!!gameState.kingInYellowAct && (
        <section className="active-heralds-under-map" aria-label="King in Yellow acts">
          <h3>King in Yellow</h3>
          <div className='king-in-yellow-act'>
            <img alt='king in yellow act' src={`/acts/act${gameState.kingInYellowAct}.jpg`} />
          </div>
        </section>
      )}
    </div>
  );
};

export const GameInfo: React.FC<GameInfoProps> = ({
  openedActionSessions,
  gameState,
  loading,
  playerName,
  message,
  selectedInvestigator,
  controlledInvestigators = [],
  currentInvestigator,
  userLanguage,
  availableActions: rawAvailableActions,
  onJoinGame,
  authRole,
  onExecuteAction,
  onRefreshGameState,
  onSelectInvestigator,
}) => {
  const mapStyle = useMapStyle();
  const desktopBlockOrder = useDesktopBlockOrder();
  const artworkStyle = useMonsterTokenStyle();
  const mobileLayout = useMobileGameLayout();
  const [newActionsOpen, setNewActionsOpen] = useState(false);
  const [tradeRevealId, setTradeRevealId] = useState<string>();
  const [actionReveal, setActionReveal] = useState<{ serial: number; slider: boolean }>();
  const [mapFocusRequest, setMapFocusRequest] = useState<InvestigatorFocusRequest>();
  const pendingMapFocus = useRef<{ gameId: string; investigatorName: string } | null>(null);
  const actionsDockRef = useRef<HTMLButtonElement>(null);
  const actionsBoundaryRef = useRef<HTMLDivElement>(null);
  const localOpenedSessions = useRef(new Set<string>());
  const openedSessions = openedActionSessions ?? localOpenedSessions.current;
  const mainContentRef = useRef<HTMLDivElement>(null);
  const lastFocusedRef = useRef<{ element: HTMLElement; title?: string | null; dockLabel?: string | null; inActions: boolean } | null>(null);
  const previousMobileLayout = useRef(mobileLayout);
  const [expandedStatsGameId, setExpandedStatsGameId] = useState<string | null>(null);
  const tracksExpanded = expandedStatsGameId === gameState.gameId;
  useEffect(() => { setExpandedStatsGameId(null); }, [gameState.gameId]);
  const [grantRefreshVersion, setGrantRefreshVersion] = useState(0);
  const [deckRefreshVersion, setDeckRefreshVersion] = useState(0);
  const [undoActionsToRemove, setUndoActionsToRemove] = useState<string>('1');
  const [undoActionsLoading, setUndoActionsLoading] = useState(false);
  const [undoActionsError, setUndoActionsError] = useState<string | null>(null);
  const [undoActionsSuccess, setUndoActionsSuccess] = useState<string | null>(null);
  const [clearNotificationsLoading, setClearNotificationsLoading] = useState(false);
  const [clearNotificationsError, setClearNotificationsError] = useState<string | null>(null);
  const [clearNotificationsSuccess, setClearNotificationsSuccess] = useState<string | null>(null);
  const [fullStateJson, setFullStateJson] = useState<string>('');
  const [fullStateLoadedGameId, setFullStateLoadedGameId] = useState<string | null>(null);
  const [fullStateActionCounter, setFullStateActionCounter] = useState<number | null>(null);
  const [fullStateLoading, setFullStateLoading] = useState(false);
  const [fullStateSaving, setFullStateSaving] = useState(false);
  const [fullStateError, setFullStateError] = useState<string | null>(null);
  const [fullStateSuccess, setFullStateSuccess] = useState<string | null>(null);
  const [isAncientOpen, setIsAncientOpen] = useState(false);
  const [isTracksOpen, setIsTracksOpen] = useState(false);
  const [dockPopup, setDockPopup] = useState<{ type: DockPopup; context: string } | null>(null);
  const [isGameLogOpen, setIsGameLogOpen] = useState(false);
  const investigatorsButtonRef = useRef<HTMLButtonElement>(null);
  const rulesButtonRef = useRef<HTMLButtonElement>(null);
  const mythosButtonRef = useRef<HTMLButtonElement>(null);
  const decksButtonRef = useRef<HTMLButtonElement>(null);
  const debugButtonRef = useRef<HTMLButtonElement>(null);
  const dockButtonRefs: DockButtonRefs = useMemo(() => ({ investigators: investigatorsButtonRef, rules: rulesButtonRef, mythos: mythosButtonRef, decks: decksButtonRef, debug: debugButtonRef }), []);
  const [fullGameLog, setFullGameLog] = useState<GameLogEntry[] | null>(null);
  const [fullGameLogGameId, setFullGameLogGameId] = useState<string | null>(null);
  const [isFullGameLogLoading, setIsFullGameLogLoading] = useState(false);
  const [fullGameLogError, setFullGameLogError] = useState<string | null>(null);
  const [showAllyPopup, setShowAllyPopup] = useState(false);
  const [revealedDeckId, setRevealedDeckId] = useState<string | null>(null);
  const [showWikiPopup, setShowWikiPopup] = useState(false);
  const [wikiDeckType, setWikiDeckType] = useState<WikiDeckType>('CommonItems');
  const [earnedAchievements, setEarnedAchievements] = useState<PlayerAchievementInGame[]>([]);
  const players = useMemo(() => gameState.players ?? [], [gameState.players]);
  const locations = gameState.locations ?? [];
  const gameLog = gameState.gameLog ?? [];
  const hasPlayerJoined = players.some(p => p.name === playerName);
  const canJoinGame = gameState.status === 'Created' && !hasPlayerJoined;
  const investigatorToShow =
    currentInvestigator ||
    findInvestigator(gameState, selectedInvestigator) ||
    getFirstControlledInvestigator(gameState, playerName);
  const isFinalBattlePhase =
    !!gameState.isFinalBattle || /final\s*battle/i.test(gameState.currentPhase ?? '');
  const isGameFinished = gameState.status === 'Finished';
  const isGameCreated = gameState.status === 'Created';
  const newActionsActive = artworkStyle === 'Digital' && !isGameCreated && !isGameFinished;
  const availableActions = useMemo(() => rawAvailableActions.filter(action =>
    action.presentationMode !== (newActionsActive ? 'Legacy' : 'NewActions')), [rawAvailableActions, newActionsActive]);
  const newMobileSheet = newActionsActive && mobileLayout;
  const navigationSections = {
    decksInline: !mobileLayout,
    rules: !isGameFinished && (buildRuleGroups(gameState).length > 0 || (!isGameCreated && !isFinalBattlePhase && locations.length > 0 && !!gameState.kingInYellowAct)),
    map: locations.length > 0 && !isGameCreated && !isGameFinished,
    mythos: !isGameFinished && (!!gameState.latestMythosSequence?.entries?.length || !!gameState.mythosDetails?.name),
  };
  const fittedLayout = mobileLayout && navigationSections.map && !isFinalBattlePhase;
  useFittedGameLayout(fittedLayout, mainContentRef, mapStyle);
  const canOpenRules = mobileLayout && navigationSections.rules;
  const cardsInPlayCount = gameState.specialCardsInPlay?.length ?? 0;
  const canOpenDecks = (!!gameState.decks || (mobileLayout && cardsInPlayCount > 0)) && !isGameFinished;
  const popupContext = `${gameState.gameId}:${isGameFinished}:${mobileLayout}`;
  const activePopup = dockPopup?.context === popupContext ? dockPopup.type : null;
  const isInvestigatorsPanelOpen = activePopup === 'investigators';
  const openDockPopup = (type: DockPopup) => { setNewActionsOpen(false); setIsGameLogOpen(false); setDockPopup({ type, context: popupContext }); };
  const closeDockPopup = () => setDockPopup(null);
  const pendingMythosPopup = useLatestMythosPopup(gameState, playerName, mobileLayout, loading,
    activePopup === 'mythos', () => {
      if (!newActionsOpen) mythosButtonRef.current?.focus({ preventScroll: true });
      setDockPopup({ type: 'mythos', context: popupContext });
    });
  useEffect(() => { setDockPopup(null); setShowWikiPopup(false); setShowAllyPopup(false); setRevealedDeckId(null); }, [popupContext]);
  useLayoutEffect(() => {
    if (previousMobileLayout.current === mobileLayout) return;
    previousMobileLayout.current = mobileLayout;
    const focus = lastFocusedRef.current;
    const focused = focus?.element;
    const title = focus?.title;
    const dockLabel = ['Investigators', 'Game Log', 'Debug panel'].includes(title ?? '') ? title
      : focus?.dockLabel;
    const inActions = focus?.inActions;
    const section = title === 'Rules in Play' ? 'game-rules' : title === 'Latest Mythos' ? 'game-latest-mythos'
      : title === 'Cards and Decks' || title === 'Decks' || title === 'Deck browser' || title === 'Allies' || title?.startsWith('Revealed ') ? (!mobileLayout && cardsInPlayCount > 0 ? 'game-cards-in-play' : 'game-decks') : undefined;
    setDockPopup(null);
    setShowWikiPopup(false);
    setShowAllyPopup(false);
    setRevealedDeckId(null);
    setIsAncientOpen(false);
    setIsTracksOpen(false);
    setIsGameLogOpen(false);
    // Child cleanup releases modal locks and restores old focus first. Then
    // replace any disconnected focus with the corresponding new surface.
    const frame = requestAnimationFrame(() => {
      if (!focused || (focused.isConnected && !focused.closest('[inert]'))) return;
      const actionTarget = newMobileSheet
        ? document.querySelector<HTMLElement>('.game-fullscreen-actions .game-dialog-close') ?? actionsDockRef.current
        : mobileLayout ? document.querySelector<HTMLElement>('.game-sheet-opener') : document.getElementById('game-actions');
      const target = (dockLabel === 'Your Turn' || dockLabel === 'Actions') && mobileLayout ? actionTarget
        : dockLabel ? document.querySelector<HTMLElement>(`.game-dock button[aria-label="${dockLabel}"]`)
        : inActions ? actionTarget
        : section ? document.getElementById(section) ?? (section === 'game-rules' ? rulesButtonRef.current : section === 'game-latest-mythos' ? mythosButtonRef.current : document.getElementById('game-cards-in-play') ?? decksButtonRef.current)
        : document.getElementById('game-map') ?? investigatorsButtonRef.current;
      target?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [mobileLayout, fittedLayout, newMobileSheet, cardsInPlayCount]);
  useEffect(() => {
    if ((dockPopup?.type === 'rules' && !canOpenRules) || (dockPopup?.type === 'mythos' && !navigationSections.mythos) || (dockPopup?.type === 'decks' && !canOpenDecks) || (dockPopup?.type === 'debug' && authRole !== 'Admin')) setDockPopup(null);
  }, [dockPopup, canOpenRules, navigationSections.mythos, canOpenDecks, authRole]);
  useEffect(() => {
    if (!canOpenDecks || !gameState.decks) { setShowWikiPopup(false); setShowAllyPopup(false); setRevealedDeckId(null); }
  }, [canOpenDecks, gameState.decks]);
  const isCurrentPlayerOwner =
    !!playerName.trim() &&
    !!gameState.owner?.trim() &&
    gameState.owner.trim().toLowerCase() === playerName.trim().toLowerCase();
  const startGameNowAction = useMemo(
    () => isCurrentPlayerOwner
      ? availableActions.find(action => action.id === START_GAME_NOW_ACTION_ID)
      : undefined,
    [availableActions, isCurrentPlayerOwner]
  );
  const displayedActions = useMemo(
    () => availableActions.filter(action => action.id !== START_GAME_NOW_ACTION_ID),
    [availableActions]
  );
  const controllerActions = displayedActions.filter(action => isControllerAction(action, playerName));
  const mobileQuickActive = mobileLayout && !isGameCreated && !isGameFinished;
  const quickModel = quickActionPresentation(controllerActions, playerName, gameState);
  const quickDirectIds = new Set(mobileQuickActive ? quickModel.selected.filter(choice => !choice.chooser).map(choice => choice.action.id) : []);
  const generalActions = (actions: UserAction[]) => newMobileSheet || fittedLayout
    ? actions.filter(action => !quickDirectIds.has(action.id) || !!action.cardType || action.card != null) : actions;
  const skillCheckFooterActions = newMobileSheet ? controllerActions.filter(action => isSkillCheckAcknowledgment(action) || isSkillCheckProgression(action)) : [];
  const scrollingControllerActions = controllerActions.filter(action => !skillCheckFooterActions.includes(action));
  const fightInvestigator = useFightInvestigator(gameState, controllerActions, newActionsActive, playerName);
  const fightExecution = useFightActionExecution({ state: gameState, investigator: fightInvestigator, actions: controllerActions,
    loading, viewerName: playerName, contentRef: mainContentRef, message, onExecuteAction });
  const fightActionProps = fightInvestigator ? {
    state: gameState, investigator: fightInvestigator, actions: scrollingControllerActions, loading,
    language: userLanguage, viewerName: playerName, onExecuteAction: fightExecution.execute,
  } : undefined;
  const upkeepDecision = newActionsActive ? getUpkeepDecision(gameState, controllerActions, playerName) : undefined;
  const upkeepActionProps = upkeepDecision ? {
    gameState, investigator: upkeepDecision.investigator, finish: upkeepDecision.finish,
    actions: scrollingControllerActions, loading, language: userLanguage, onExecuteAction,
  } : undefined;
  const playerCount = gameState.players?.length ?? gameState.playersDetailed?.length ?? gameState.playersOrder?.length ?? 0;
  const normalizedPlayerName = playerName.trim().toLowerCase();
  const canShowTurnInvestigatorSheet = playerCount <= 1 || (
    !!normalizedPlayerName && gameState.activePlayer?.trim().toLowerCase() === normalizedPlayerName
  );
  const sliderChoice = availableActions.find(action =>
    action.id.startsWith('set_') && action.sliderPosition != null && !!action.investigatorName &&
    (!action.playerName || action.playerName.trim().toLowerCase() === normalizedPlayerName)
  );
  const sliderInvestigator = findInvestigator(gameState, sliderChoice?.investigatorName) ??
    (sliderChoice?.investigatorName?.toLowerCase() === investigatorToShow?.name.toLowerCase() ? investigatorToShow : undefined);
  const inlineInvestigator = sliderInvestigator ?? investigatorToShow;
  const showInlineInvestigatorSheet = !upkeepDecision && !fightActionProps && !!inlineInvestigator && (
    !!sliderInvestigator || /^Skills Placement$/i.test(gameState.currentPhase ?? '') ||
    (canShowTurnInvestigatorSheet && /^Upkeep$/i.test(gameState.currentPhase ?? ''))
  );
  const sheetActions = availableActions
    .filter(action => (!action.playerName || action.playerName.trim().toLowerCase() === normalizedPlayerName) &&
      (!action.investigatorName || action.investigatorName.toLowerCase() === inlineInvestigator?.name.toLowerCase()))
    .map(action => ({ ...action, investigatorName: inlineInvestigator?.name, disabled: loading || !!action.disabled }));
  const mythosInformation = gameState.mythosInformation?.trim();
  const encounterInformation = gameState.encounterInformation?.trim();
  const damageInformation = gameState.damageInformation?.trim();
  const skillCheckInformation = gameState.skillCheckInformation;
  const otherWorldEncounterInformation = gameState.otherWorldEncounterInformation?.trim();
  const finalBattleInformation = gameState.finalBattleInformation?.trim();
  const combatMonsters = gameState.currentCombatMonsters || [];
  const currentFightingMonster = gameState.currentFightingMonster;
  const showCombatBanner = combatMonsters.length >= 2;
  const hasPreviewContext = !!(mythosInformation || encounterInformation || otherWorldEncounterInformation
    || (!fightActionProps && (currentFightingMonster || showCombatBanner)) || finalBattleInformation || damageInformation || skillCheckInformation);
  const includeDunwichInCurrentGame =
    enabledBoards(gameState).dunwich;
  const includeLKingsportInCurrentGame =
    enabledBoards(gameState).kingsport;
  const includeInnsmouthInCurrentGame =
    enabledBoards(gameState).innsmouth;
  const deckDisplay = useMemo<DeckDisplayEntry[]>(() => {
    const decks = gameState.decks;
    const fallback: DeckInfo = { count: 0 };
    const entries: Omit<DeckDisplayEntry, 'imagePath'>[] = [
      { id: 'uniqueItems', label: 'Unique Items', info: decks?.uniqueItems ?? fallback, wikiDeckType: 'UniqueItems' },
      { id: 'exhibits', label: 'Exhibits', info: decks?.exhibits ?? fallback, wikiDeckType: 'Exhibits' },
      { id: 'commonItems', label: 'Common Items', info: decks?.commonItems ?? fallback, wikiDeckType: 'CommonItems' },
      { id: 'spells', label: 'Spells', info: decks?.spells ?? fallback, wikiDeckType: 'Spells' },
      { id: 'skills', label: 'Skills', info: decks?.skills ?? fallback, wikiDeckType: 'Skills' },
      { id: 'allies', label: 'Allies', info: decks?.allies ?? fallback },
      { id: 'madnesses', label: 'Madnesses', info: decks?.madnesses ?? fallback, wikiDeckType: 'Madness' },
      { id: 'injuries', label: 'Injuries', info: decks?.injuries ?? fallback, wikiDeckType: 'Injuries' },
      { id: 'cultEncounters', label: 'Cult Encounters', info: decks?.cultEncounters ?? fallback, wikiDeckType: 'CultEncounters' },
      {
        id: 'corruptions',
        label: 'Corruptions',
        info: decks?.corruptions ?? fallback,
        wikiDeckType: 'Corruptions',
      },
      { id: 'monsters', label: 'Monsters', info: decks?.monsters ?? fallback, wikiDeckType: 'Monsters' },
      { id: 'gates', label: 'Gates', info: decks?.gates ?? fallback, wikiDeckType: 'Gates' },
    ];
    return entries.map(entry => ({ ...entry, imagePath: getCardBackPath(entry.id, artworkStyle, decks?.corruptions?.topCardColor ?? undefined) }));
  }, [gameState.decks, artworkStyle]);
  const revealedDeck = deckDisplay.find(deck => deck.id === revealedDeckId && deck.info.count !== 0);
  const revealedCard = !isGameFinished ? revealedDeck?.info.bottomCard : undefined;
  useEffect(() => { setRevealedDeckId(null); }, [gameState.gameId]);
  useEffect(() => {
    if (!revealedCard) setRevealedDeckId(null);
  }, [revealedCard]);
  const gameOutcome = (gameState.data && gameState.data["gameOutcome"]) as string | undefined;
  const epilogueMessage = (gameState.data && gameState.data["message"]) as string | undefined;
  const investigatorLookup = useMemo<Record<string, Investigator>>(
    () => gameState.investigatorsByName ?? {},
    [gameState.investigatorsByName]
  );

  const finalBattleInvestigators = useMemo(
    () => orderedGameInvestigators(gameState),
    [gameState]
  );
  const dockInvestigators = useMemo(() => orderedDockInvestigators(gameState), [gameState]);
  const statisticsInvestigators = useMemo<InvestigatorStatisticsTableEntry[]>(() => {
    const entries: InvestigatorStatisticsTableEntry[] = [];
    const seenNames = new Set<string>();

    finalBattleInvestigators.forEach((investigator) => {
      if (!investigator?.statistics || !investigator.name) {
        return;
      }

      entries.push({
        name: investigator.name,
        statistics: investigator.statistics,
        isDevoured: !!investigator.isDevoured,
      });
      seenNames.add(investigator.name.toLowerCase());
    });

    (gameState.investigatorsStats || []).forEach((snapshot) => {
      if (!snapshot?.investigatorName || !snapshot.statistics) {
        return;
      }

      const nameKey = snapshot.investigatorName.toLowerCase();
      if (seenNames.has(nameKey)) {
        return;
      }

      entries.push({
        name: snapshot.investigatorName,
        statistics: snapshot.statistics,
        isDevoured: true,
      });
      seenNames.add(nameKey);
    });

    return entries;
  }, [finalBattleInvestigators, gameState.investigatorsStats]);
  const isFullLogLoaded = fullGameLogGameId === gameState.gameId && fullGameLog !== null;
  const displayedGameLog = isFullLogLoaded ? fullGameLog : gameLog;
  const undoActionsCount = Number.parseInt(undoActionsToRemove, 10);
  const canUndoActions = Number.isInteger(undoActionsCount) && undoActionsCount > 0;

  useEffect(() => {
    setFullGameLog(null);
    setFullGameLogGameId(null);
    setFullGameLogError(null);
    setIsFullGameLogLoading(false);
    setUndoActionsToRemove('1');
    setUndoActionsError(null);
    setUndoActionsSuccess(null);
    setFullStateJson('');
    setFullStateLoadedGameId(null);
    setFullStateActionCounter(null);
    setFullStateError(null);
    setFullStateSuccess(null);
  }, [gameState.gameId]);

  useEffect(() => {
    if (!isGameFinished || !playerName.trim() || !gameState.gameId) {
      setEarnedAchievements([]);
      return;
    }

    let cancelled = false;

    const loadEarnedAchievements = async () => {
      try {
        const achievements = await gameApi.getPlayerAchievementsByGame(
          playerName,
          gameState.gameId
        );

        if (!cancelled) {
          setEarnedAchievements(achievements);
        }
      } catch {
        if (!cancelled) {
          setEarnedAchievements([]);
        }
      }
    };

    loadEarnedAchievements();

    return () => {
      cancelled = true;
    };
  }, [gameState.gameId, isGameFinished, playerName]);

  const renderStatisticsPanel = () => {
    if (!isGameFinished || !statisticsInvestigators.length) return null;

    const isVictory = gameState?.data?.gameOutcome === 'Victory';
    const aliveInvestigators = finalBattleInvestigators.filter((i) => !i.isDevoured).length;
    const gateTrophiesCount = finalBattleInvestigators.reduce((sum, inv) => sum + (inv.gateTrophies?.length || 0), 0);
    const monsterTrophiesPoints = Math.floor(finalBattleInvestigators.reduce((sum, inv) => sum + (inv.monsterTrophies?.length || 0), 0) / 3);
    const activeHeraldsPoints = (gameState.activeHeralds?.length || 0) * 3;
    const loans = finalBattleInvestigators.filter((i) => !!i.loanTaken || i.canTakeLoan === false).length;
    const elderSignUsed = gameState?.statistics?.elderSignUsed || 0;
    const missionsAndTasksCompleted = gameState?.statistics?.missionsCompleted || 0;
    const terrorLevel = gameState?.terrorLevel || 0;
    const doomTrack = gameState?.ancientOne?.maximumDoomTrack || 0;

    const RenderValue = ({title, value}: {title: string; value: number}) => {
      const className = value < 0 ? 'red' : value > 0 ? 'green' : '';
      return (
        <>
          <div className='victory-title'>
           {title}:
          </div>
          <div className={`victory-value ${className}`}>
            {value}
          </div>
        </>
      );
    }

    return (
      <div>
        {earnedAchievements.length > 0 && (
          <div className="statistics-panel earned-achievements-panel">
            <h4>Earned achievements</h4>
            <div className="achievements-grid">
              {earnedAchievements.map((achievement) => (
                <AchievementCard
                  key={`${achievement.name}-${achievement.gameId}-${achievement.receivedAt}`}
                  achievement={achievement}
                  isReceived={true}
                />
              ))}
            </div>
          </div>
        )}
        {!!isVictory && (
          <div className="statistics-panel">
            <h4>Victory points</h4>
            <div className="victory-points">
              <RenderValue title='Doom Track' value={doomTrack} />
              <RenderValue title='Alive Investigators' value={aliveInvestigators} />
              <RenderValue title='Gate Trophies' value={gateTrophiesCount} />
              <RenderValue title='Monster Trophies' value={monsterTrophiesPoints} />
              <RenderValue title='Missions and Tasks' value={missionsAndTasksCompleted} />
              <RenderValue title='Active Heralds' value={activeHeraldsPoints} />
              <RenderValue title='Terror Level' value={terrorLevel * -1} />
              <RenderValue title='Loans' value={loans * -1} />
              <RenderValue title='Elder Signs Used' value={elderSignUsed * -1} />
              <RenderValue title="Total" value={doomTrack + aliveInvestigators + gateTrophiesCount + monsterTrophiesPoints + missionsAndTasksCompleted + activeHeraldsPoints - terrorLevel - loans - elderSignUsed} />
            </div>
          </div>
        )}
        <div className='support-end-game'>
          <Support />
        </div>
        <div className="statistics-panel">
          <h4>Investigator Statistics</h4>
          <div className="statistics-table-wrapper">
            <table className="statistics-table">
              <thead>
                <tr>
                  <th>Statistic</th>
                  {statisticsInvestigators.map((investigator) => (
                    <th key={investigator.name}>
                      {investigator.name}
                    </th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {statisticRows.map((row) => {
                  const values = statisticsInvestigators.map((investigator) => {
                    return investigator.statistics[row.key] ?? 0;
                  });
                  const bestValue =
                    row.preference === 'high'
                      ? Math.max(...values)
                      : row.preference === 'low'
                        ? Math.min(...values)
                        : null;
                  const total = values.reduce((sum, value) => sum + value, 0);
                  const formatValue = (value: number) => {
                    return row.format ? row.format(value) : value.toString();
                  };
                  if (row.skipZeros && total === 0) {
                    return null;
                  }

                  return (
                    <tr key={row.key}>
                      <th scope="row">{row.label}</th>
                      {values.map((value, index) => {
                        const isBest = bestValue !== null && value === bestValue;
                        const bestClassName =
                          row.preference === 'high'
                            ? 'statistics-cell-best-high'
                            : row.preference === 'low'
                              ? 'statistics-cell-best-low'
                              : '';

                        return (
                          <td
                            key={`${row.key}-${statisticsInvestigators[index].name}`}
                            className={isBest ? bestClassName : undefined}
                          >
                            {formatValue(value)}
                          </td>
                        );
                      })}
                      <td className="statistics-cell-total">{formatValue(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const investigatorPlayerMap = useMemo(() => {
    const mapping: Record<string, string> = {};

    players.forEach(player => {
      (player.controlledInvestigators || []).forEach(investigatorName => {
        if (investigatorName) {
          mapping[investigatorName] = player.name;
        }
      });
    });

    return mapping;
  }, [players]);

  const handleUndoActions = async () => {
    if (!gameState.gameId || !canUndoActions) {
      return;
    }

    setUndoActionsLoading(true);
    setUndoActionsError(null);
    setUndoActionsSuccess(null);
    try {
      const result = await gameApi.adminUndoActions(gameState.gameId, undoActionsCount);
      setGrantRefreshVersion(version => version + 1);
      setDeckRefreshVersion(version => version + 1);
      setUndoActionsSuccess(`Removed ${result.undoneCount} actions. Remaining: ${result.remainingActions}.`);
      await onRefreshGameState?.();
    } catch (err) {
      setUndoActionsError(err instanceof Error ? err.message : 'Failed to undo actions');
    } finally {
      setUndoActionsLoading(false);
    }
  };

  const handleClearNotificationMessageIds = async () => {
    if (!gameState.gameId) {
      return;
    }

    setClearNotificationsLoading(true);
    setClearNotificationsError(null);
    setClearNotificationsSuccess(null);
    try {
      const result = await gameApi.adminClearNotificationMessageIds(gameState.gameId);
      setClearNotificationsSuccess(
        `Cleared ${result.clearedCount} notification ids for ${result.playersCount} players.`
      );
    } catch (err) {
      setClearNotificationsError(
        err instanceof Error ? err.message : 'Failed to clear notification message ids'
      );
    } finally {
      setClearNotificationsLoading(false);
    }
  };

  const handleLoadFullStateJson = async () => {
    if (!gameState.gameId) {
      return;
    }

    setFullStateLoading(true);
    setFullStateError(null);
    setFullStateSuccess(null);
    try {
      const fullState = await gameApi.getAdminGameFullState(gameState.gameId);
      setFullStateJson(fullState.fullStateJson ?? '');
      setFullStateLoadedGameId(fullState.gameId);
      setFullStateActionCounter(fullState.fullStateActionCounter ?? null);
      setFullStateSuccess('FullStateJson loaded from database.');
    } catch (err) {
      setFullStateError(err instanceof Error ? err.message : 'Failed to load game full state');
    } finally {
      setFullStateLoading(false);
    }
  };

  const handleSaveFullStateJson = async () => {
    if (!gameState.gameId || !fullStateJson.trim()) {
      return;
    }

    try {
      JSON.parse(fullStateJson);
    } catch {
      setFullStateError('FullStateJson is invalid');
      setFullStateSuccess(null);
      return;
    }

    if (!window.confirm('Overwrite this game FullStateJson in the database?')) {
      return;
    }

    setFullStateSaving(true);
    setFullStateError(null);
    setFullStateSuccess(null);
    try {
      const result = await gameApi.updateAdminGameFullState(gameState.gameId, fullStateJson);
      setGrantRefreshVersion(version => version + 1);
      setDeckRefreshVersion(version => version + 1);
      setFullStateLoadedGameId(result.gameId);
      setFullStateSuccess(result.message);
      await onRefreshGameState?.();
    } catch (err) {
      setFullStateError(err instanceof Error ? err.message : 'Failed to update game full state');
    } finally {
      setFullStateSaving(false);
    }
  };

  const handleLoadFullGameLog = async () => {
    if (!gameState.gameId) {
      return;
    }

    setIsFullGameLogLoading(true);
    setFullGameLogError(null);

    try {
      const gameLog = await gameApi.getGameLog(playerName, gameState.gameId);
      setFullGameLog(gameLog);
      setFullGameLogGameId(gameState.gameId);
    } catch (err) {
      setFullGameLogError(err instanceof Error ? err.message : 'Failed to load full game log');
    } finally {
      setIsFullGameLogLoading(false);
    }
  };

  const renderSkillValue = (label: string, baseValue: number, breakdown?: SkillBreakdown) => {
    const effectiveBase = breakdown?.base ?? baseValue;
    const total = breakdown?.total ?? baseValue;
    const color = total > effectiveBase ? '#3fb950' : total < effectiveBase ? '#ff6b6b' : undefined;

    const wrapperClass =
      label === 'Snk' || label === 'Wil'
        ? 'margin-bottom-4'
        : undefined;

    return (
      <div className={wrapperClass}>
        {label} <b style={color ? { color } : undefined}>{total}</b>
      </div>
    );
  };

  const omittedControls = mobileQuickActive ? controllerActions.filter(action => !quickDirectIds.has(action.id) && (
    skillCheckFooterActions.includes(action) ||
    (!!fightActionProps && !!fightControlKind(action, currentFightingMonster)) ||
    (!!upkeepActionProps && (action === upkeepActionProps.finish || isUndoAction(action)))
  )) : [];
  const revealActions = (action?: UserAction) => {
    closeDockPopup(); setIsGameLogOpen(false);
    if (newMobileSheet || fittedLayout) setNewActionsOpen(true);
    if (action?.trade) {
      setTradeRevealId(action.id);
      setActionReveal(undefined);
    } else setActionReveal(previous => ({ serial: (previous?.serial ?? 0) + 1, slider: action?.sliderPosition != null }));
  };
  useEffect(() => {
    if (!actionReveal || !mobileQuickActive) return;
    if ((newMobileSheet || fittedLayout) && !newActionsOpen) return;
    const frame = requestAnimationFrame(() => {
      const target = document.querySelector<HTMLElement>(actionReveal.slider
        ? '.game-fullscreen-actions .investigator-digital-skills, .game-action-sheet .inline-upkeep-investigator-sheet, .game-skill-actions, .inline-upkeep-investigator-sheet'
        : '#game-actions');
      if (!target) return;
      target.tabIndex = -1;
      target.scrollIntoView?.({ block: 'nearest' });
      target.focus({ preventScroll: true });
      setActionReveal(undefined);
    });
    return () => cancelAnimationFrame(frame);
  }, [actionReveal, mobileQuickActive, newActionsOpen, newMobileSheet, fittedLayout]);
  const quickRow = mobileQuickActive && quickModel.selected.length > 0 ? <QuickActionRow choices={quickModel.selected}
    hasMore={quickModel.hasMore} loading={loading} onMore={() => revealActions()}
    onChoose={choice => {
      if (choice.chooser) revealActions(choice.action);
      else if (fightActionProps) fightExecution.execute(choice.action.id);
      else onExecuteAction(choice.action.id);
    }} /> : undefined;

  const renderActionButtons = () => <section id="game-actions" tabIndex={-1} aria-label="Actions">
    {newActionsActive && controllerActions.some(action => action.trade?.direction === 'finish')
      ? <TradeActions key={gameState.gameId} state={gameState} actions={scrollingControllerActions} loading={loading} language={userLanguage} onExecuteAction={onExecuteAction} requestedActionId={tradeRevealId} onRequestHandled={() => setTradeRevealId(undefined)} />
      : fightActionProps ? <FightActions {...fightActionProps} controlsInline={!mobileQuickActive} />
      : upkeepActionProps ? <UpkeepActions {...upkeepActionProps} controlsInline={!mobileQuickActive} />
      : <ActionsList compact={newActionsActive} actions={mobileQuickActive ? generalActions(newActionsActive ? scrollingControllerActions : controllerActions) : newActionsActive ? scrollingControllerActions : displayedActions} language={userLanguage} loading={loading} onExecuteAction={onExecuteAction} />}
    {omittedControls.length > 0 && <ActionsList compact actions={omittedControls} loading={loading} language={userLanguage}
      getActionLabel={action => isSkillCheckAcknowledgment(action) ? 'OK' : action.name}
      onExecuteAction={fightActionProps ? fightExecution.execute : onExecuteAction} />}
  </section>;
  const renderActionContext = () => (!fightActionProps || hasPreviewContext || (!fittedLayout && !newMobileSheet && !!message?.trim())) ? (<div
          className="sticky-actions-wrapper"
        >
          {mythosInformation && (
            <div className="mythos-information-banner">
              <span className="mythos-information-label">Mythos:</span>{' '}
              <span className="mythos-information-text">{mythosInformation}</span>
            </div>
          )}
          {encounterInformation && (
            <div className="encounter-information-banner">
              <span className="encounter-information-label">Encounter:</span>{' '}
              <span className="encounter-information-text">{encounterInformation}</span>
            </div>
          )}
          {otherWorldEncounterInformation && (
            <div className="otherworld-information-banner">
              <span className="otherworld-information-label">Other World:</span>{' '}
              <span className="otherworld-information-text">{otherWorldEncounterInformation}</span>
            </div>
          )}
          {currentFightingMonster && !fightActionProps && (
            <div className="current-monster-banner">
              <div className="current-monster-label">Fighting:</div>
              <div className="current-monster-name">{currentFightingMonster.name}</div>
              {!!gameState.dunwichHorrorCurrentRollInfo && currentFightingMonster.name === 'The Dunwich Horror' && artworkStyle !== 'Digital' && (
                <div>
                  <img alt={gameState.dunwichHorrorCurrentRollInfo} src={`/Monsters/${gameState.dunwichHorrorCurrentRollInfo}.png`} />
                </div>
              )}
              <div className='current-monster-wrapper'>
                <MonsterDetailCard monster={currentFightingMonster} />
              </div>
            </div>
          )}
          {showCombatBanner && !fightActionProps && (
            <div className="current-combat-banner">
              <span className="current-combat-label">Current combat:</span>
              <MonstersDetails monsters={combatMonsters} />
            </div>
          )}
          {finalBattleInformation && (
            <div className="final-battle-information-banner">
              <span className="final-battle-information-label">Final Battle:</span>{' '}
              <span className="final-battle-information-text">{finalBattleInformation}</span>
            </div>
          )}
          {damageInformation && (
            <div className="damage-information-banner">
              <span className="damage-information-text">{damageInformation}</span>
            </div>
          )}
          {!!skillCheckInformation && (
            <SkillCheckInfo skillCheckInfo={skillCheckInformation} />
          )}
          {!fittedLayout && !newMobileSheet && <GameMessage message={message} />}
        </div>) : null;
  const renderInlineInvestigatorSheet = () => (showInlineInvestigatorSheet && inlineInvestigator && (
          <section className="inline-upkeep-investigator-sheet" tabIndex={0} aria-label={`${inlineInvestigator.name} investigator sheet`}>
            <PlayerHeader gameState={gameState} investigator={inlineInvestigator} />
            <InvestigatorDisplay gameState={gameState}
              investigator={inlineInvestigator}
              availableActions={sheetActions}
              onExecuteAction={id => { if (!loading) onExecuteAction(id); }}
              currentLocationName={locations.find(location => location.investigatorNames?.includes(inlineInvestigator.name))?.name}
              showStats
              deckInfo={{ commonItems: gameState.decks?.commonItems, uniqueItems: gameState.decks?.uniqueItems, spells: gameState.decks?.spells }}
              language={userLanguage}
            />
          </section>
        ));
  const hasDecisionContext = !!(mythosInformation || encounterInformation || otherWorldEncounterInformation || currentFightingMonster || showCombatBanner || finalBattleInformation || damageInformation || skillCheckInformation || message?.trim() || showInlineInvestigatorSheet);
  const hasPendingAction = controllerActions.some(action => !action.disabled && !isUndoAction(action));
  const toggleNewActions = () => {
    pendingMapFocus.current = null;
    if (!newActionsOpen) { closeDockPopup(); setIsGameLogOpen(false); }
    setNewActionsOpen(value => !value);
  };
  const statusMessage = message?.trim() || [mythosInformation, encounterInformation, otherWorldEncounterInformation,
    currentFightingMonster ? `Fighting: ${currentFightingMonster.name}` : undefined,
    showCombatBanner ? 'Current combat' : undefined, finalBattleInformation, damageInformation,
    skillCheckInformation ? `${skillCheckInformation.skillName || skillCheckInformation.baseSkillName || 'Skill'} check` : undefined,
  ].filter(Boolean).join('\n');
  const movementActor = gameState.currentPhase === 'Movement' && !isFinalBattlePhase
    ? controllerActions.find(action => !action.disabled && !isUndoAction(action) && action.cardType !== 'Investigator' && action.investigatorName)?.investigatorName
    : undefined;
  const actorControlled = movementActor && players.some(player => player.name.trim().toLowerCase() === normalizedPlayerName
    && player.controlledInvestigators?.some(name => name.toLowerCase() === movementActor.toLowerCase()));
  const movementActorLocation = actorControlled && locations.find(location => location.investigatorNames?.some(name => name.toLowerCase() === movementActor.toLowerCase()));
  const showMovementOnMap = newMobileSheet && !!movementActorLocation && navigationSections.map;
  const completeMapMinimization = () => {
    const request = pendingMapFocus.current;
    pendingMapFocus.current = null;
    if (request?.gameId === gameState.gameId) setMapFocusRequest(previous => ({ id: (previous?.id ?? 0) + 1, investigatorName: request.investigatorName }));
  };
  const upkeepSessionKey = upkeepDecision?.key;
  useEffect(() => {
    setNewActionsOpen(false); setMapFocusRequest(undefined); pendingMapFocus.current = null;
    setTradeRevealId(undefined); setActionReveal(undefined);
  }, [gameState.gameId, mobileLayout, artworkStyle]);
  useEffect(() => {
    if (!mobileQuickActive || (!newMobileSheet && !fittedLayout)) { pendingMapFocus.current = null; setNewActionsOpen(false); return; }
    if (!newMobileSheet) return;
    if (loading || pendingMythosPopup || activePopup === 'mythos') return;

    if (upkeepSessionKey && hasPendingAction && !openedSessions.has(upkeepSessionKey)) {
      openedSessions.add(upkeepSessionKey);
      setNewActionsOpen(true);
    }
  }, [mobileQuickActive, fittedLayout, newMobileSheet, loading, upkeepSessionKey, hasPendingAction, openedSessions, pendingMythosPopup, activePopup]);
  const renderOverview = (part?: 'all' | 'status' | 'tracks' | 'investigator') => <GameStatusSummary part={part} state={gameState} investigator={investigatorToShow} playerName={playerName}
    mobileActions={newMobileSheet ? { pending: hasPendingAction, open: newActionsOpen, onOpen: toggleNewActions, message: statusMessage } : undefined}
    collapsible={mobileLayout}
    tracksExpanded={tracksExpanded} onToggleTracks={() => setExpandedStatsGameId(tracksExpanded ? null : gameState.gameId)}
    onInvestigators={() => { openDockPopup('investigators'); }} onAncientOne={() => { setNewActionsOpen(false); setIsAncientOpen(true); }} onTracks={() => { setNewActionsOpen(false); setIsTracksOpen(true); }} />;
  const renderCardsInPlay = () => cardsInPlayCount > 0 ? (
    <section id="game-cards-in-play" className="specialCardsInPlay" tabIndex={-1} aria-label="Cards in Play">
      <h3>Cards in Play</h3>
      <div className="cards-in-play-grid">{gameState.specialCardsInPlay!.map((card, index) => (
        <Card key={`${card.name}-${index}`} card={{ item: card, kind: 'special' }} />
      ))}</div>
    </section>
  ) : null;
  const renderDecks = () => (
    <section id="game-decks" className="deck-section" tabIndex={-1} aria-label="Decks">
      <h3 className="deck-section-heading">Decks</h3>
      <div className={`deck-grid${artworkStyle === 'Digital' ? ' deck-grid-digital' : ''}${deckDisplay.some(d => d.info.count !== 0 && d.info.bottomCard) ? ' deck-grid-revealed' : ''}`}>
        {deckDisplay.map(deck => {
          if (deck.info.count === 0) return null;
          const canBrowse = !!deck.wikiDeckType || (deck.id === 'allies' && (deck.info.cards?.length ?? 0) > 0);
          const bottomCardTitle = deck.info.bottomCard && 'title' in deck.info.bottomCard
            ? deck.info.bottomCard.title?.trim() || deck.info.bottomCard.name
            : deck.info.bottomCard?.name;
          const deckFace = <>
            <div className="deck-image-wrapper">
              <img loading="lazy" src={deck.imagePath} alt={`${deck.label} deck`} className={`deck-image${deck.id === 'gates' ? ' gate-back-artwork' : ''}`} />
              {artworkStyle !== 'Digital' && <div className="deck-count-badge">{deck.info.count}</div>}
            </div>
            <div className="deck-label">{deck.label}</div>
            {artworkStyle === 'Digital' && <div className="deck-count-badge deck-count-below">{deck.info.count}</div>}
          </>;
          return (
          <div
            key={deck.id}
            className={`deck-card-visual${artworkStyle === 'Digital' && deck.id !== 'monsters' && deck.id !== 'gates' ? ' digital-deck-card' : ''} ${canBrowse ? 'active' : ''}`}
          >
            {canBrowse ? <button type="button" className="deck-browse" aria-label={`Browse ${deck.label} deck, ${deck.info.count} ${deck.info.count === 1 ? 'card' : 'cards'}`} onClick={() => {
              if (deck.id === 'allies' && (deck.info.cards?.length ?? 0) > 0) {
                setShowAllyPopup(true);
                return;
              }

              if (deck.wikiDeckType) {
                setWikiDeckType(deck.wikiDeckType);
                setShowWikiPopup(true);
              }
            }}>{deckFace}</button> : <div className="deck-browse">{deckFace}</div>}
            {deck.info.bottomCard && <>
              <div className="deck-bottom-card" onClick={(e) => e.stopPropagation()}>
                <Card card={{
                  item: deck.info.bottomCard,
                  kind: getItemKind(deck.id)
                }} />
              </div>
              <button type="button" className="deck-revealed-button" onClick={() => setRevealedDeckId(deck.id)}>
                Revealed: {bottomCardTitle}
              </button>
            </>}
          </div>
          );
        })}
        {/* {gameState.decks?.encounters && <EncountersDecks decks={gameState.decks?.encounters} />} */}
      </div>
    </section>
  );

  const renderMap = () => locations.length > 0 && !isGameCreated && !isGameFinished ? (
    <section id="game-map" tabIndex={-1} aria-label="Map">
      <GameMapFrame stats={mobileLayout ? renderOverview('tracks') : undefined}>
      {mapStyle === 'Reworked' ? <DigitalMapDisplay
        presentation={mobileLayout ? 'camera' : 'document'}
        focusRequest={mapFocusRequest}
        key={`${gameState.gameId}:${mobileLayout}`}
        compact={fittedLayout}
        gameId={gameState.gameId}
        gameState={gameState}
        locationActions={availableActions}
        loading={loading}
        onExecuteAction={onExecuteAction}
        investigatorsByName={investigatorLookup}
        currentInvestigator={investigatorToShow}
        highlightedInvestigators={controlledInvestigators}
        viewerName={playerName}
        locations={locations}
        authLanguage={userLanguage || 'En'}
        includeDunwichCards={includeDunwichInCurrentGame}
        includeKingsportCards={includeLKingsportInCurrentGame}
        includeInnsmouthInCurrentGame={includeInnsmouthInCurrentGame}
      /> : <TabbedMapDisplay
        focusRequest={mapFocusRequest}
        key={`${gameState.gameId}:${mobileLayout}`}
        presentation={mobileLayout ? 'camera' : 'document'}
        gameId={gameState.gameId}
        compact={fittedLayout}
        includeDunwichCards={includeDunwichInCurrentGame}
        includeKingsportCards={includeLKingsportInCurrentGame}
        includeInnsmouthInCurrentGame={includeInnsmouthInCurrentGame}
        locations={locations}
        investigatorsByName={investigatorLookup}
        terrorLevel={gameState.terrorLevel}
        authLanguage={userLanguage || 'En'}
        dunwichHorrorTerrorTrack={gameState.dunwichHorrorTerrorTrack}
        activeRumorLocationName={gameState.activeRumor?.rumorLocation || gameState.activeRumor?.activityLocationName}
        activeMythosLocationName={gameState.activeMythos?.rumorLocation || gameState.activeMythos?.activityLocationName}
        activeProcessLocationName={gameState.activeProcess?.rumorLocation || gameState.activeProcess?.activityLocationName}
        maximumMonstersInArkham={gameState.maximumMonstersInArkham}
        outskirtsLimit={gameState.outskirtsLimit}
        maximumGates={gameState.maximumGatesAllowed}
        highlightedInvestigators={controlledInvestigators}
        currentInvestigator={investigatorToShow}
        locationActions={availableActions}
        viewerName={playerName}
        loading={loading}
        onExecuteAction={onExecuteAction}
        investigatorPlayerMap={investigatorPlayerMap}
        gameState={gameState}
        deepOnesRisingTrack={gameState.deepOnesRisingTrack}
        fedsRaidInnsmouthTrack={gameState.fedsRaidInnsmouthTrack}
        deckInfo={{
          commonItems: gameState.decks?.commonItems,
          uniqueItems: gameState.decks?.uniqueItems,
          spells: gameState.decks?.spells,
        }}
        riftProgress={gameState.riftProgress}
      />}
      </GameMapFrame>
    </section>
  ) : null;
  const desktopBlocks: Partial<Record<DesktopBlock, React.ReactNode>> = mobileLayout ? {} : {
    actions: <>{renderActionContext()}{renderActionButtons()}{renderInlineInvestigatorSheet()}</>,
    map: renderMap(),
    rules: !isGameFinished && <GameRules state={gameState} actions={availableActions} />,
    cards: <>{renderCardsInPlay()}{canOpenDecks && renderDecks()}</>,
    mythos: !isGameFinished && <LatestMythos state={gameState} />,
  };

  return (
    <MonsterInspectionContext.Provider value={{ state: gameState, viewerName: playerName }}><MiskatonicTraumaProvider enabled={!!gameState.gameSettings?.includeMiskatonicCards}><SpellCastingPreviewsProvider previews={gameState.spellCastingPreviews}><ItemPreviewsProvider previews={gameState.itemCardPreviews}><div onFocusCapture={event => {
      const element = event.target as HTMLElement;
      // Record the source before responsive rendering can detach its ancestors.
      lastFocusedRef.current = { element,
        title: element.closest('[role="dialog"]')?.querySelector('.game-dialog-title h2')?.getAttribute('aria-label'),
        dockLabel: element.closest('.game-dock-links button')?.getAttribute('aria-label'),
        inActions: !!element.closest('.game-quick-actions, .game-action-sheet, .game-fullscreen-actions, #game-actions, .inline-upkeep-investigator-sheet') || !!document.querySelector('.game-action-sheet[aria-modal="true"], .game-fullscreen-actions[aria-modal="true"]') };
    }} className={`game-info${fittedLayout ? ' game-fitted' : ''}`}>
      {/* Player's Investigator Sidebar */}
      {investigatorToShow && !(fightActionProps && !mobileLayout) && (
        <div className="player-investigator-sidebar">
          {controlledInvestigators.length > 1 && onSelectInvestigator && (
            <div className="investigator-switcher" onClick={e => e.stopPropagation()}>
              <label htmlFor="investigator-switcher-select">Investigator:</label>
              <select
                id="investigator-switcher-select"
                value={selectedInvestigator || investigatorToShow.name}
                onChange={e => onSelectInvestigator(e.target.value)}
              >
                {controlledInvestigators.map(name => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            className="investigator-toggle-tab"
            onClick={() => {
              openDockPopup('investigators');
            }}
            title="Open Investigators popup"
            aria-expanded={isInvestigatorsPanelOpen}
          >
            <div className="investigator-toggle-meta">
              <span className="investigator-toggle-label">{investigatorToShow?.name}</span>
            </div>
            <div className="investigator-quick-stats">
              <div className="quick-stat-chip">
                <div className="quick-stat-stamina">
                  {investigatorToShow.stamina}/
                  <span style={getStatColor(investigatorToShow.maxStaminaBreakdown) ? { color: getStatColor(investigatorToShow.maxStaminaBreakdown) } : undefined}>
                    {investigatorToShow.maxStamina}
                  </span>
                </div>
              </div>
              <div className="quick-stat-chip">
                <div className="quick-stat-sanity">
                  {investigatorToShow.sanity}/
                  <span style={getStatColor(investigatorToShow.maxSanityBreakdown) ? { color: getStatColor(investigatorToShow.maxSanityBreakdown) } : undefined}>
                    {investigatorToShow.maxSanity}
                  </span>
                </div>
              </div>
              <div className="quick-stat-chip">
                <div className="quick-stat-clue">
                  {investigatorToShow.clues}
                </div>
              </div>
              <div className="quick-stat-chip money-inline">
                <div className="quick-stat-money">
                  {investigatorToShow.money}
                </div>
              </div>
              <div className="quick-stat-chip skills">
                {renderSkillValue('Spd', investigatorToShow.speed, investigatorToShow.speedBreakdown)}
                {renderSkillValue('Snk', investigatorToShow.sneak, investigatorToShow.sneakBreakdown)}
                {renderSkillValue('Fgt', investigatorToShow.fight, investigatorToShow.fightBreakdown)}
                {renderSkillValue('Wil', investigatorToShow.will, investigatorToShow.willBreakdown)}
                {renderSkillValue('Lor', investigatorToShow.lore, investigatorToShow.loreBreakdown)}
                {renderSkillValue('Lck', investigatorToShow.luck, investigatorToShow.luckBreakdown)}
              </div>
            </div>
          </button>
        </div>
      )}

      <div key={String(mobileLayout)} ref={mainContentRef} className="game-main-content">
        {!mobileLayout && renderOverview('tracks')}
        {newMobileSheet && <div ref={actionsBoundaryRef} className="game-actions-boundary" />}
        {fittedLayout ? <>{renderOverview('investigator')}{renderOverview('status')}</> : <>{renderOverview('status')}{renderOverview('investigator')}</>}

        {mobileLayout && !fittedLayout && !newMobileSheet && renderActionContext()}

        {mobileLayout && !fittedLayout && !newMobileSheet && renderInlineInvestigatorSheet()}
        <div className="game-status" inert={newMobileSheet && newActionsOpen ? true : undefined}>
          {!fittedLayout && (isGameCreated || isGameFinished) && <h2>
            Game Status: <span className={`status status-${gameState.status}`}>{gameState.status}</span>
          </h2>}

          {gameState.status === 'Created' && (
            <>
              {gameState.gameSettings?.isPrivate && <span className='warning'>This is private game, it doesn't show up in the list of games. Send <a href={window.location.pathname + window.location.search}>the link to this page</a> to other players so they can join it.</span>}
            </>
          )}
          
          {canJoinGame && (
            <div className="join-game-section">
              <p>You haven't joined this game yet.</p>
              <button onClick={onJoinGame} disabled={loading}>
                Join Game
              </button>
            </div>
          )}
          

          {locations.length > 0 && (mobileLayout || isGameCreated || isGameFinished || isFinalBattlePhase) && (
            <div className="map-or-ancient-container">
              {isGameCreated ? (
                <CreatedGameInfo
                  gameState={gameState}
                  loading={loading}
                  startGameNowAction={startGameNowAction}
                  onExecuteAction={onExecuteAction}
                />
              ) :
              isGameFinished ? (
              <div className={`game-outcome-panel ${gameOutcome === 'Defeat' ? 'defeat' : ''}`}>
                <h3>{gameOutcome || 'Game Over'}</h3>
                {epilogueMessage && <p className="game-outcome-text">{epilogueMessage}</p>}
                {!epilogueMessage && (
                  <p className="game-outcome-text">The struggle has ended. Consult the game log for details.</p>
                )}
                {renderStatisticsPanel()}
              </div>
            ) : (
              <>
                {isFinalBattlePhase ? (
                  <FinalBattlePanel
                    ancientOne={gameState.ancientOne}
                    highlightedInvestigators={controlledInvestigators}
                    investigators={finalBattleInvestigators}
                    gameState={gameState}
                    language={userLanguage}
                    onExecuteAction={onExecuteAction}
                    currentInvestigator={currentInvestigator}
                  />
                ) : null
              }
                {mobileLayout && renderMap()}
                {mobileLayout && !fittedLayout && !newMobileSheet && renderActionButtons()}
              </>
            )}
            </div>
          )}

          {!mobileLayout && desktopBlockOrder.map(block => <React.Fragment key={block}>{desktopBlocks[block]}</React.Fragment>)}

          {(fittedLayout || newMobileSheet) && <GameActionSheet key={`${gameState.gameId}:${newMobileSheet}`} hasActions={displayedActions.length > 0} hasContext={hasDecisionContext} hasPreviewContext={hasPreviewContext} message={message} loading={loading}
            fullscreen={newMobileSheet ? { open: newActionsOpen, blocked: activePopup === 'mythos', onMinimize: () => setNewActionsOpen(false), dockTargetRef: actionsDockRef,
              boundaryRef: actionsBoundaryRef,
              onMinimized: completeMapMinimization,
              headerActions: showMovementOnMap ? <button type="button" className="game-show-on-map" onClick={() => {
                pendingMapFocus.current = { gameId: gameState.gameId, investigatorName: movementActor! };
                setNewActionsOpen(false);
              }}>Show on the Map</button> : undefined,
              controls: quickRow } : undefined}
            mobileControls={!newMobileSheet && mobileQuickActive ? { open: newActionsOpen, onOpenChange: setNewActionsOpen, row: quickRow } : undefined}
            context={hasPreviewContext ? renderActionContext() : null} actions={renderActionButtons()} investigatorSheet={renderInlineInvestigatorSheet()} />}

          {mobileLayout && !navigationSections.map && !newMobileSheet && renderActionButtons()}

          {!fittedLayout && locations.length > 0 && !isGameCreated && !isGameFinished && <ActiveEntitiesUnderMap
            gameState={gameState} isFinalBattlePhase={isFinalBattlePhase} />}
        </div>

      </div>

      <React.Fragment key={`inspectors:${popupContext}`}>
      {isAncientOpen && <AncientOneDetails state={gameState} onClose={() => setIsAncientOpen(false)} />}
      {isTracksOpen && <GameTracksDetails state={gameState} language={userLanguage} onClose={() => setIsTracksOpen(false)} />}
      {(investigatorToShow || finalBattleInvestigators[0]) && <InvestigatorInspector key={`investigators:${popupContext}`}
        state={gameState} investigator={investigatorToShow || finalBattleInvestigators[0]} investigators={finalBattleInvestigators}
        viewerName={playerName} selectedInvestigatorName={investigatorToShow?.name}
        open={isInvestigatorsPanelOpen} dockTargetRef={investigatorsButtonRef}
        actions={availableActions} loading={loading} language={userLanguage} onExecuteAction={onExecuteAction} onClose={closeDockPopup} />}
      </React.Fragment>
      {fightExecution.confirmation}
      <GameDock quickActions={!(newMobileSheet || fittedLayout) || !newActionsOpen ? quickRow : undefined} blockOrder={mobileLayout ? undefined : desktopBlockOrder} hideTurn={fittedLayout} actions={displayedActions} loading={loading} phase={gameState.currentPhase} investigators={dockInvestigators}
        actionPanel={newMobileSheet ? { available: true, pending: hasPendingAction, open: newActionsOpen,
          targetRef: actionsDockRef, onOpen: toggleNewActions } : undefined}
        showMap={!mobileLayout}
        sections={navigationSections} buttonRefs={dockButtonRefs} onExecuteAction={onExecuteAction}
        onInvestigators={() => { openDockPopup('investigators'); }}
        onLog={() => { setNewActionsOpen(false); closeDockPopup(); setIsGameLogOpen(true); }}
        onRules={mobileLayout ? () => openDockPopup('rules') : undefined}
        onMythos={mobileLayout ? () => openDockPopup('mythos') : undefined}
        onDecks={canOpenDecks || (!mobileLayout && cardsInPlayCount > 0) ? () => mobileLayout ? openDockPopup('decks') : jumpToGameSection(cardsInPlayCount > 0 ? 'game-cards-in-play' : 'game-decks') : undefined}
        onDebug={authRole === 'Admin' ? () => openDockPopup('debug') : undefined} />
      <React.Fragment key={`dock-dialogs:${popupContext}`}>
      {authRole === 'Admin' && <GameDialog key={`debug:${popupContext}`} title="Debug panel" open={activePopup === 'debug'} dockTargetRef={debugButtonRef} onClose={closeDockPopup}>
        <div className="debug-content">
          <div className="debug-grant-form">
            <div>
              <label>
                Undo actions:
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={undoActionsToRemove}
                  onChange={e => setUndoActionsToRemove(e.target.value)}
                />
              </label>
            </div>
            <button
              onClick={handleUndoActions}
              disabled={undoActionsLoading || !canUndoActions}
            >
              {undoActionsLoading ? 'Undoing...' : 'Undo N Actions'}
            </button>
          </div>
          {undoActionsError && <div className="error">{undoActionsError}</div>}
          {undoActionsSuccess && <div>{undoActionsSuccess}</div>}
          <div className="admin-actions-row">
            <button
              onClick={handleClearNotificationMessageIds}
              disabled={clearNotificationsLoading}
            >
              {clearNotificationsLoading ? 'Clearing...' : 'Clear Notification Message IDs'}
            </button>
          </div>
          {clearNotificationsError && <div className="error">{clearNotificationsError}</div>}
          {clearNotificationsSuccess && <div>{clearNotificationsSuccess}</div>}
          <div className="admin-full-state-editor">
            <div className="admin-full-state-header">
              <h4>FullStateJson</h4>
              <div className="admin-actions-row">
                <button
                  onClick={handleLoadFullStateJson}
                  disabled={fullStateLoading || fullStateSaving}
                >
                  {fullStateLoading ? 'Loading...' : 'Load From DB'}
                </button>
                <button
                  onClick={handleSaveFullStateJson}
                  disabled={fullStateLoading || fullStateSaving || !fullStateJson.trim()}
                >
                  {fullStateSaving ? 'Saving...' : 'Save To DB'}
                </button>
              </div>
            </div>
            <textarea
              className="admin-full-state-textarea"
              value={fullStateJson}
              onChange={e => {
                setFullStateJson(e.target.value);
                setFullStateError(null);
                setFullStateSuccess(null);
              }}
              spellCheck={false}
              placeholder="Load FullStateJson from the database"
            />
            <div className="admin-hint">
              Game: {fullStateLoadedGameId || gameState.gameId}
              {fullStateActionCounter !== null ? ` | Checkpoint action: ${fullStateActionCounter}` : ''}
            </div>
            {fullStateError && <div className="error">{fullStateError}</div>}
            {fullStateSuccess && <div>{fullStateSuccess}</div>}
          </div>
          <GameData key={`game-data-${gameState.gameId}`} data={gameState} />
          <AdminGrantPanel
            key={`grant-${gameState.gameId}`}
            gameId={gameState.gameId}
            defaultInvestigator={investigatorToShow?.name}
            refreshVersion={grantRefreshVersion}
            onRefreshGameState={onRefreshGameState}
            onCheckpointChanged={() => setDeckRefreshVersion(version => version + 1)}
          />
          <AdminDeckPanel
            key={`deck-${gameState.gameId}`}
            gameId={gameState.gameId}
            refreshVersion={deckRefreshVersion}
            onCheckpointChanged={() => setGrantRefreshVersion(version => version + 1)}
            onRefreshGameState={onRefreshGameState}
          />
        </div>
      </GameDialog>}
      {canOpenRules && <GameDialog key={`rules:${popupContext}`} title="Rules in Play" open={activePopup === 'rules'} dockTargetRef={rulesButtonRef} onClose={closeDockPopup}>
        <GameRules state={gameState} actions={availableActions} hideHeading includeAct={fittedLayout} />
      </GameDialog>}
      {mobileLayout && navigationSections.mythos && <GameDialog key={`mythos:${popupContext}`} title="Latest Mythos" open={activePopup === 'mythos'} dockTargetRef={mythosButtonRef} onClose={closeDockPopup}>
        <LatestMythos state={gameState} hideHeading />
      </GameDialog>}
      {canOpenDecks && <DeckSurface mobile={mobileLayout} open={activePopup === 'decks'} targetRef={decksButtonRef} onClose={() => { setShowWikiPopup(false); setShowAllyPopup(false); setRevealedDeckId(null); closeDockPopup(); }}>
        {mobileLayout && <>{renderCardsInPlay()}{gameState.decks && renderDecks()}</>}
        {showWikiPopup && <GameDialog title="Deck browser" onClose={() => setShowWikiPopup(false)}>
          <MonsterInspectionEnabled.Provider value={false}><WikiView onBack={() => setShowWikiPopup(false)} initialDeckType={wikiDeckType} initialLanguage={userLanguage} initialIncludeDunwichCards={includeDunwichInCurrentGame} initialIncludeMiskatonicCards={!!gameState.gameSettings?.includeMiskatonicCards} /></MonsterInspectionEnabled.Provider>
        </GameDialog>}
        {showAllyPopup && <GameDialog title="Allies" variant="allies" onClose={() => setShowAllyPopup(false)}>
          <div className="ally-popup-grid">{(gameState.decks?.allies?.fullCards ?? []).map(card => <Card key={card.name} card={{ item: card, kind: 'ally' }} />)}</div>
        </GameDialog>}
        {revealedDeck && revealedCard && <GameDialog title={`Revealed ${revealedDeck.label} card`} onClose={() => setRevealedDeckId(null)}>
          <div className="game-item-artwork"><Card card={{ item: revealedCard, kind: getItemKind(revealedDeck.id) }} /></div>
        </GameDialog>}
      </DeckSurface>}
      {isGameLogOpen && <GameDialog title="Game Log" variant="log" onClose={() => setIsGameLogOpen(false)} header={<div className="game-log-toolbar">
        <button type="button" className="game-log-load-full-button" onClick={handleLoadFullGameLog} disabled={isFullGameLogLoading}>{isFullGameLogLoading ? 'Loading…' : isFullLogLoaded ? 'Reload Full Log' : 'Load Full Log'}</button>
        {!isFullLogLoaded && gameLog.length >= 50 && <p className="game-log-limit-note">Showing the latest 50 entries.</p>}
        {fullGameLogError && <p className="game-log-load-error" role="alert">{fullGameLogError}</p>}
      </div>}>
        <GameLog log={displayedGameLog} />
      </GameDialog>}

      </React.Fragment>
    </div></ItemPreviewsProvider></SpellCastingPreviewsProvider></MiskatonicTraumaProvider></MonsterInspectionContext.Provider>
  );
};

/** Desktop deck inspectors stand alone; on mobile they nest in the dock popup. */
const DeckSurface: React.FC<{ mobile: boolean; open: boolean; targetRef: React.RefObject<HTMLButtonElement | null>; onClose: () => void; children: React.ReactNode }> = ({ mobile, open, targetRef, onClose, children }) => mobile
  ? <GameDialog title="Cards and Decks" open={open} dockTargetRef={targetRef} onClose={onClose}>{children}</GameDialog>
  : <>{children}</>;
